import { FC, useContext, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useCountTo } from "../../hooks/useCountTo";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { PaginationQuery } from "../../types";
import { formatNumber } from "../../types/utils";
import styles from "./CarStats.module.css";

type CarStatsProps = {
  query: PaginationQuery;
};

const CarStats: FC<CarStatsProps> = ({ query }) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();
  const { subsidiaryId } = useParams<{ subsidiaryId: string }>();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const filters = query.filters ?? [];

  const statsResult = carsApi.useCarStats(
    {
      ...query,
      filters: subsidiaryId ? [{ fieldname: "car.subsidiary.id", values: [subsidiaryId] }, ...filters] : filters,
    },
    aborter.current.signal,
  );

  useEffect(() => {
    statsResult.error && dispatch(showErrorNotification(i18n, statsResult.error));
  }, [statsResult.error]);

  const stats = [
    {
      number: statsResult.data?.totalActive,
      title: i18n.t.common.active,
      color: "#b3ffc2",
      borderColor: "#4aa55c80",
    },
    {
      number: statsResult.data?.totalAssigned,
      title: i18n.t.common.assigned,
      color: "#ffffad",
      borderColor: "#9b9b5280",
    },
    {
      number: statsResult.data?.totalNotInUse,
      title: i18n.t.common.notInUse,
      color: "#fa8383ff",
      borderColor: "#93474750",
    },
    {
      number: statsResult.data?.totalCarPool,
      title: i18n.t.common.carPool,
      color: "#ee72a1ff",
      borderColor: "#8b444750",
    },
    {
      number: statsResult.data?.avgAgeYears,
      title: i18n.t.common.avgYears,
      color: "#ffdd83",
      borderColor: "#9b854c50",
      decimals: 1,
    },
  ];

  return (
    <div className={styles["stats-container"]}>
      {stats.map((stat) => (
        <StatCard
          key={stat.title}
          number={stat.number}
          title={stat.title}
          color={stat.color}
          borderColor={stat.borderColor}
          isLoading={!statsResult.data}
          decimals={stat.decimals}
          language={i18n.language}
        />
      ))}
    </div>
  );
};
export default CarStats;

type StatCardProps = {
  number?: number;
  decimals?: number;
  title: string;
  color: string;
  borderColor: string;
  isLoading: boolean;
  language: string;
};

const StatCard: FC<StatCardProps> = ({ number, title, color, borderColor, isLoading, decimals, language }) => {
  const animatedNumber = useCountTo({ target: number, isReady: !isLoading, decimals: decimals });

  return (
    <div className={styles["stat-card"]} style={{ backgroundColor: color, borderColor }}>
      <div className={styles["number-container"]}>
        <div className={styles["stat-number"]}>
          {formatNumber(animatedNumber, language, { fractionalDigits: decimals ?? 0 })}
        </div>
      </div>
      <div className={styles["stat-title"]}>{title}</div>
    </div>
  );
};
